import * as React from "react";
import axios from "axios";
import { api, absoluteBackendUrl } from "@/lib/api";


import { featuredGames, discountGames, categories as defaultCategories } from "@/data/mockGames";

// Cache keys - used for instant loading AND fallback
const CACHE_KEY = "crackoyun_cache_v3";

// Load cached data - for instant display on load
function loadCache() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached);
      // Check if cache is recent (within 10 minutes) for instant display
      if (data.timestamp && Date.now() - data.timestamp < 10 * 60 * 1000) {
        return data;
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

// Save data to cache
function saveCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      ...data,
      timestamp: Date.now()
    }));
  } catch {
    // Ignore cache errors
  }
}

// Clear old cache on load
function clearOldCache() {
  try {
    localStorage.removeItem("crackoyun_cache"); // Remove old cache key
    localStorage.removeItem("crackoyun_cache_v2"); // Remove old cache key
  } catch {
    // Ignore
  }
}

function safeId() {
  if (typeof crypto !== "undefined" && crypto?.randomUUID) return crypto.randomUUID();
  return `id_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

const StoreContext = React.createContext(null);

// NOTE: Previously this prototype stored everything in localStorage.
// Now we start integrating with backend APIs.
// We still keep localStorage only for small UX (e.g., admin token) and fallback.

const seedGames = () => {
  const now = Date.now();
  const base = [
    ...featuredGames.map((g, idx) => ({
      id: g.id,
      title: g.title,
      description: g.tagline,
      cover: g.cover,
      gallery: [
        g.cover,
        featuredGames[(idx + 1) % featuredGames.length].cover,
        featuredGames[(idx + 2) % featuredGames.length].cover,
      ],
      trailer: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
      requirements:
        "OS: Windows 10 (64-bit)\nCPU: Intel i5 / Ryzen 5\nRAM: 8 GB (16 GB önerilir)\nGPU: GTX 1060 / RX 580\nDirectX: 12\nStorage: 50 GB SSD\nNetwork: Broadband Internet\nNotes: İlk açılışta shader derleme olabilir.",
      downloads: 1200 + idx * 127,
      favorites: 340 + idx * 19,
      rating: g.rating,
      category: idx % 2 === 0 ? "New" : "Recently Added",
      links: [
        { label: "Download 1", url: "#" },
        { label: "Download 2", url: "#" },
        { label: "Download 3", url: "#" },
      ],
      createdAt: now - idx * 1000 * 60 * 60 * 7,
    })),
    ...discountGames.map((g, idx) => ({
      id: g.id,
      title: g.title,
      description: `${g.genre} — limited-time deal.`,
      cover: g.cover,
      gallery: [
        g.cover,
        discountGames[(idx + 1) % discountGames.length].cover,
        discountGames[(idx + 2) % discountGames.length].cover,
      ],
      trailer: "https://www.youtube.com/watch?v=aqz-KE-bpKQ",
      requirements:
        "OS: Windows 10 (64-bit)\nCPU: Intel i5 / Ryzen 5\nRAM: 8 GB (16 GB önerilir)\nGPU: GTX 1060 / RX 580\nDirectX: 12\nStorage: 50 GB SSD\nNetwork: Broadband Internet\nNotes: İlk açılışta shader derleme olabilir.",
      downloads: 740 + idx * 93,
      favorites: 120 + idx * 12,
      rating: 4.2 + (idx % 4) * 0.15,
      category: idx % 2 === 0 ? "PUBG" : "Recently Added",
      links: [
        { label: "Download 1", url: "#" },
        { label: "Download 2", url: "#" },
        { label: "Download 3", url: "#" },
      ],
      createdAt: now - idx * 1000 * 60 * 60 * 11,
    })),
  ];

  // Ensure stable unique ids
  return base.map((g, i) => ({ ...g, id: `${g.id}-${i}` }));
};

export const StoreProvider = ({ children }) => {
  // Load cache immediately for instant display
  const cachedData = React.useMemo(() => {
    clearOldCache();
    return loadCache();
  }, []);
  
  const defaultCats = [
    { id: "new", title: "Yeni Oyunlar" },
    { id: "pubg", title: "PUBG" },
    ...defaultCategories.slice(0, 2).map((c) => ({
      id: c.id,
      title: c.title,
    })),
  ];

  // Start with cached data for instant display, then refresh from API
  const [settings, setSettings] = React.useState(cachedData?.settings || null);
  const [announcement, setAnnouncement] = React.useState(cachedData?.settings?.announcement || "🎮 CrackOyun — oyunları kategorilere göre keşfet.");
  const [telegramUrl, setTelegramUrl] = React.useState(cachedData?.settings?.telegramUrl || "");
  const [maintenanceMode, setMaintenanceMode] = React.useState(cachedData?.settings?.maintenanceMode || false);
  const [maintenanceMessage, setMaintenanceMessage] = React.useState(cachedData?.settings?.maintenanceMessage || "");
  const [footerLinks, setFooterLinks] = React.useState(cachedData?.settings?.footerLinks || {});
  const [adLinkEnabled, setAdLinkEnabled] = React.useState(cachedData?.settings?.adLinkEnabled || false);
  const [adLinkUrl, setAdLinkUrl] = React.useState(cachedData?.settings?.adLinkUrl || "");
  const [adLinkDelay, setAdLinkDelay] = React.useState(cachedData?.settings?.adLinkDelay || 5);
  const [backgroundImage, setBackgroundImage] = React.useState(cachedData?.settings?.backgroundImage || "");
  const [backgroundSize, setBackgroundSize] = React.useState(cachedData?.settings?.backgroundSize || 100);
  const [smokeEffectEnabled, setSmokeEffectEnabled] = React.useState(cachedData?.settings?.smokeEffectEnabled || false);
  const [logo, setLogo] = React.useState(cachedData?.settings?.logo || "");
  const [favicon, setFavicon] = React.useState(cachedData?.settings?.favicon || "");
  const [logoBackgroundColor, setLogoBackgroundColor] = React.useState(cachedData?.settings?.logoBackgroundColor || "");
  const [logoBackgroundTransparent, setLogoBackgroundTransparent] = React.useState(cachedData?.settings?.logoBackgroundTransparent || false);
  const [siteTitleColor, setSiteTitleColor] = React.useState(cachedData?.settings?.siteTitleColor || "");
  const [googleMapsApiKey, setGoogleMapsApiKey] = React.useState(cachedData?.settings?.googleMapsApiKey || "");
  const [splashTheme, setSplashTheme] = React.useState(cachedData?.settings?.splashTheme || "circuit");
  const [googleClientId, setGoogleClientId] = React.useState(cachedData?.settings?.googleClientId || "");
  const [googleClientSecret, setGoogleClientSecret] = React.useState(cachedData?.settings?.googleClientSecret || "");
  const [splashAdEnabled, setSplashAdEnabled] = React.useState(cachedData?.settings?.splashAdEnabled ?? true);
  const [splashAdUrl, setSplashAdUrl] = React.useState(cachedData?.settings?.splashAdUrl || "https://lnk.news/e4Snni");
  
  // Start with cached data or defaults
  const [categories, setCategories] = React.useState(cachedData?.categories?.length ? cachedData.categories : defaultCats);
  const [games, setGames] = React.useState(cachedData?.games?.length ? cachedData.games : []);
  const [slider, setSlider] = React.useState(cachedData?.slider?.length ? cachedData.slider : []);
  
  // Pagination state
  const [pagination, setPagination] = React.useState({
    page: 1,
    limit: 16,
    total: cachedData?.games?.length || 0,
    totalPages: Math.ceil((cachedData?.games?.length || 0) / 16) || 1
  });
  const [gamesLoading, setGamesLoading] = React.useState(false);
  
  // Programs state
  const [programs, setPrograms] = React.useState([]);
  
  // Giveaway state
  const [giveaway, setGiveaway] = React.useState(cachedData?.giveaway || null);
  
  // Track if initial load is done - start as true if we have cached data
  const [isLoaded, setIsLoaded] = React.useState(!!cachedData?.games?.length);
  const [isLoading, setIsLoading] = React.useState(!cachedData?.games?.length);

  const [recentDownloads, setRecentDownloads] = React.useState([]);

  const sanitizeRequirements = (req) => {
    const s = String(req || "");
    if (!s) return s;
    if (s.includes(" • ")) return s.split(" • ").join("\n");
    if (s.includes("•")) return s.split("•").map((x) => x.trim()).filter(Boolean).join("\n");
    return s;
  };

  const mapGameFromApi = (g, categoryMap) => {
    const images = (g.images || []).map((u) => absoluteBackendUrl(u));
    const cover = images[0] || "";
    const gallery = images.length ? images : [cover, cover, cover].filter(Boolean);

    // Handle both string and object requirements
    let requirements = "";
    if (typeof g.requirements === "string") {
      requirements = g.requirements;
    } else if (g.requirements?.min || g.requirements?.recommended) {
      const reqMin = g.requirements?.min ? `MIN: ${g.requirements.min}` : "";
      const reqRec = g.requirements?.recommended
        ? `ÖNERİLEN: ${g.requirements.recommended}`
        : "";
      requirements = [reqMin, reqRec].filter(Boolean).join("\n\n");
    }

    // Parse createdAt from API (ISO string) to timestamp
    let createdAt = Date.now();
    if (g.createdAt) {
      const parsed = new Date(g.createdAt).getTime();
      if (!isNaN(parsed)) {
        createdAt = parsed;
      }
    }

    return {
      id: g.id,
      title: g.title,
      description: g.description,
      cover,
      gallery,
      trailer: g.trailerUrl || null,  // Only show trailer if URL exists in database
      requirements: sanitizeRequirements(requirements || ""),
      downloads: g.downloadCount || 0,
      favorites: g.favorites || 0,
      isFavorite: false,
      rating: g.rating || 0,
      categoryId: g.categoryId,
      category: categoryMap?.[g.categoryId] || "",
      links: (g.downloads || []).map((d, idx) => ({
        label: d.name || `Download ${idx + 1}`,
        url: d.url || "#",
        platform: d.platform || "PC",
      })),
      createdAt,
      comments: [],
      commentCount: g.commentCount || 0,
    };
  };


  const loadApprovedComments = async (gameId) => {
    try {
      const res = await api.get(`/games/${gameId}/comments`);
      const items = (res.data || []).map((c) => ({
        id: c.id,
        text: c.content,
        at: Date.parse(c.createdAt) || Date.now(),
        userName: c.userName || "Guest",
      }));
      setGames((prev) =>
        prev.map((g) => (g.id === gameId ? { ...g, comments: items } : g))
      );
      return items;
    } catch {
      return [];
    }
  };

  const refresh = async () => {
    // If we have cached data, don't show loading
    if (!cachedData?.games?.length) {
      setIsLoading(true);
    }
    console.log("[StoreContext] Starting refresh...");
    
    // Record visit in background (don't wait)
    api.post("/stats/visit", {
      page: window.location.pathname,
      referrer: document.referrer || "",
    }).catch(() => {});

    const baseUrl = process.env.REACT_APP_BACKEND_URL;

    try {
      // Make all API calls in PARALLEL using native fetch for speed
      const results = await Promise.allSettled([
        fetch(`${baseUrl}/api/settings`).then(r => r.json()),
        fetch(`${baseUrl}/api/categories`).then(r => r.json()),
        fetch(`${baseUrl}/api/slider`).then(r => r.json()),
        fetch(`${baseUrl}/api/games?page=1&limit=16`).then(r => r.json()),
        fetch(`${baseUrl}/api/giveaway`).then(r => r.json()),
      ]);
      
      console.log("[StoreContext] All API calls completed");

      // Extract results
      const settingsData = results[0].status === 'fulfilled' ? results[0].value : {};
      const catsData = results[1].status === 'fulfilled' ? results[1].value : [];
      const sliderData = results[2].status === 'fulfilled' ? results[2].value : [];
      const gamesData = results[3].status === 'fulfilled' ? results[3].value : { games: [], pagination: {} };
      const giveawayData = results[4].status === 'fulfilled' ? results[4].value : null;

      // Process settings
      if (Object.keys(settingsData).length) {
        setSettings(settingsData);
      }
      
      setAnnouncement(settingsData?.announcement || "🎮 CrackOyun — oyunları kategorilere göre keşfet.");
      setTelegramUrl(settingsData?.telegramUrl || "");
      setMaintenanceMode(settingsData?.maintenanceMode || false);
      setMaintenanceMessage(settingsData?.maintenanceMessage || "");
      setFooterLinks(settingsData?.footerLinks || {});
      setAdLinkEnabled(settingsData?.adLinkEnabled || false);
      setAdLinkUrl(settingsData?.adLinkUrl || "");
      setAdLinkDelay(settingsData?.adLinkDelay || 5);
      setBackgroundImage(settingsData?.backgroundImage || "");
      setBackgroundSize(settingsData?.backgroundSize || 100);
      setSmokeEffectEnabled(settingsData?.smokeEffectEnabled || false);
      setLogo(settingsData?.logo || "");
      setFavicon(settingsData?.favicon || "");
      setLogoBackgroundColor(settingsData?.logoBackgroundColor || "");
      setLogoBackgroundTransparent(settingsData?.logoBackgroundTransparent || false);
      setSiteTitleColor(settingsData?.siteTitleColor || "");
      setGoogleMapsApiKey(settingsData?.googleMapsApiKey || "");
      setSplashTheme(settingsData?.splashTheme || "circuit");
      setGoogleClientId(settingsData?.googleClientId || "");
      setGoogleClientSecret(settingsData?.googleClientSecret || "");
      setSplashAdEnabled(settingsData?.splashAdEnabled || false);
      setSplashAdUrl(settingsData?.splashAdUrl || "https://lnk.news/e4Snni");

      // Process categories
      const cats = (catsData || []).map((c) => ({
        id: c.id,
        title: c.name,
        slug: c.slug,
      }));
      const usedCats = cats.length ? cats : defaultCats;
      setCategories(usedCats);

      const categoryMap = Object.fromEntries(
        usedCats.map((c) => [c.id, c.title])
      );

      // Process slider
      const slides = (sliderData || []).map((s) => ({
        id: s.id,
        title: s.title,
        subtitle: s.description,
        image: absoluteBackendUrl(s.imageUrl),
        link: s.link || "#",
        videoUrl: s.videoUrl || null,
        badgeType: s.badgeType || "none",
        sortOrder: s.sortOrder || 0,
      }));
      if (slides.length) setSlider(slides);

      // Process giveaway
      if (giveawayData?.enabled) {
        setGiveaway(giveawayData);
      } else {
        setGiveaway(null);
      }

      // Process games
      const gamesList = gamesData?.games || [];
      const paginationData = gamesData?.pagination || { page: 1, limit: 16, total: gamesList.length, totalPages: 1 };
      
      const apiGames = gamesList.map((g) => mapGameFromApi(g, categoryMap));
      console.log("[StoreContext] Games loaded:", apiGames.length);
      if (apiGames.length) {
        setGames(apiGames);
      }
      
      setPagination(paginationData);
      
      // Save to cache for offline fallback
      saveCache({
        settings: settingsData,
        categories: usedCats,
        slider: slides.length ? slides : undefined,
        games: apiGames.length ? apiGames : undefined,
        giveaway: giveawayData?.enabled ? giveawayData : undefined,
      });
      
      setIsLoaded(true);
      setIsLoading(false);
    } catch (err) {
      console.error("[StoreContext] API Error:", err);
      // API failed - try to load from cache as fallback
      const cachedData = loadCache();
      if (cachedData) {
        if (cachedData.settings) setSettings(cachedData.settings);
        if (cachedData.categories?.length) setCategories(cachedData.categories);
        if (cachedData.slider?.length) setSlider(cachedData.slider);
        if (cachedData.games?.length) setGames(cachedData.games);
      } else {
        // No cache - use mock data
        setGames(seedGames());
        setSlider(featuredGames.map((g) => ({
          id: g.id,
          title: g.title,
          subtitle: g.tagline,
          image: g.cover,
          link: "#",
        })));
      }
      setIsLoaded(true);
      setIsLoading(false);
    }
  };

  // Function to load specific page of games
  const lastLoadRef = React.useRef({ page: null, categoryId: null, searchQuery: null });
  
  const loadGamesPage = React.useCallback(async (page, categoryId = null, searchQuery = null, sort = null) => {
    // Skip if same request as last time (prevent duplicate loads)
    const last = lastLoadRef.current;
    if (last.page === page && last.categoryId === categoryId && last.searchQuery === searchQuery && last.sort === sort) {
      console.log("[StoreContext] Skipping duplicate load request");
      return;
    }
    
    // Update last load ref
    lastLoadRef.current = { page, categoryId, searchQuery, sort };
    
    setGamesLoading(true);
    
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/games?page=${page}&limit=16`;
      if (categoryId) {
        url += `&categoryId=${categoryId}`;
      }
      if (searchQuery && searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }
      if (sort) {
        url += `&sort=${sort}`;
      }
      
      console.log("[StoreContext] Loading page", page, "category:", categoryId, "search:", searchQuery);
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      const gamesData = data.games || [];
      const paginationData = data.pagination || { page, limit: 16, total: 0, totalPages: 1 };
      
      // Build category map from current categories
      const categoryMap = Object.fromEntries(
        categories.map((c) => [c.id, c.title])
      );
      
      const apiGames = gamesData.map((g) => mapGameFromApi(g, categoryMap));
      setGames(apiGames);
      setPagination(paginationData);
      
      console.log("[StoreContext] Page loaded:", apiGames.length, "games, total:", paginationData.total);
    } catch (e) {
      console.error("[StoreContext] Load page failed:", e.message);
    } finally {
      setGamesLoading(false);
    }
  }, [categories]);

  // Load programs by category - only loads when a specific category is selected
  const loadProgramsByCategory = React.useCallback(async (categoryId = null) => {
    // If no category selected (Tüm Oyunlar), don't show programs
    if (!categoryId) {
      setPrograms([]);
      return [];
    }
    
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/programs?categoryId=${categoryId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });
      
      if (!response.ok) {
        setPrograms([]);
        return [];
      }
      
      const data = await response.json();
      setPrograms(data || []);
      return data || [];
    } catch (e) {
      console.error("[StoreContext] Load programs failed:", e.message);
      setPrograms([]);
      return [];
    }
  }, []);

  React.useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addCategory = (title) => {
    const id = `cat_${Math.random().toString(16).slice(2)}`;
    setCategories((prev) => [...prev, { id, title }]);
  };

  const addGame = (payload) => {
    const id = `game_${Math.random().toString(16).slice(2)}`;
    const now = Date.now();
    setGames((prev) => [
      {
        id,
        title: payload.title,
        description: payload.description,
        cover: payload.cover,
        gallery: payload.gallery?.length ? payload.gallery : [payload.cover, payload.cover, payload.cover],
        trailer: payload.trailer || "",
        requirements: payload.requirements,
        downloads: 0,
        favorites: 0,
        rating: 0,
        category: payload.category,
        links: payload.links,
        createdAt: now,
      },
      ...prev,
    ]);
  };

  const updateGame = (id, patch) => {
    setGames((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  };

  const deleteGame = (id) => {
    setGames((prev) => prev.filter((g) => g.id !== id));
  };

  const toggleFavorite = (id) => {
    setGames((prev) =>
      prev.map((g) =>
        g.id === id ? { ...g, isFavorite: !g.isFavorite, favorites: (g.favorites ?? 0) + (g.isFavorite ? -1 : 1) } : g
      )
    );
  };

  const rateGame = async (id, value) => {
    // Generate a simple user identifier (stored in localStorage)
    let visitorId = localStorage.getItem('crackoyun_visitor_id');
    if (!visitorId) {
      visitorId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('crackoyun_visitor_id', visitorId);
    }
    
    // Try to save to backend first
    try {
      const response = await api.post(`/games/${id}/rate`, { 
        rating: value,
        userIp: visitorId 
      });
      
      // Update locally with the returned average
      if (response.ok) {
        setGames((prev) =>
          prev.map((g) =>
            g.id === id
              ? {
                  ...g,
                  rating: response.avgRating || value,
                  ratingCount: response.ratingCount || 1,
                }
              : g
          )
        );
      }
    } catch {
      // Fallback - update locally
      setGames((prev) =>
        prev.map((g) =>
          g.id === id
            ? {
                ...g,
                rating: value,
              }
            : g
        )
      );
    }
  };

  const addComment = async (gameId, comment, userName, userEmail, isAdminComment = false) => {
    const content = String(comment || "").trim();
    const name = String(userName || "").trim();
    const email = String(userEmail || "").trim();
    
    if (!content) return { ok: false };
    if (!name) return { ok: false, error: "İsim gerekli" };
    if (!email) return { ok: false, error: "Email gerekli" };

    try {
      await api.post("/comments", {
        gameId,
        userName: name,
        userEmail: email,
        content,
        isAdmin: isAdminComment, // Admin comments are auto-approved
      });
      // Comment is pending approval (unless admin), so we don't append it to approved list.
      return { ok: true, pending: !isAdminComment };
    } catch {
      return { ok: false };
    }
  };

  const recordDownload = async (id) => {
    // Optimistic UI
    setGames((prev) =>
      prev.map((g) =>
        g.id === id ? { ...g, downloads: (g.downloads ?? 0) + 1 } : g
      )
    );

    setRecentDownloads((prev) => {
      const g = games.find((x) => x.id === id);
      if (!g) return prev;
      const entry = { id: g.id, title: g.title, cover: g.cover, at: Date.now() };
      const next = [entry, ...prev.filter((x) => x.id !== id)].slice(0, 6);
      return next;
    });

    try {
      await api.post(`/games/${id}/download`);
      return { ok: true };
    } catch {
      return { ok: false };
    }
  };

  const value = {
    settings,
    announcement,
    setAnnouncement,
    telegramUrl,
    setTelegramUrl,
    maintenanceMode,
    setMaintenanceMode,
    maintenanceMessage,
    setMaintenanceMessage,
    footerLinks,
    setFooterLinks,
    adLinkEnabled,
    setAdLinkEnabled,
    adLinkUrl,
    setAdLinkUrl,
    adLinkDelay,
    setAdLinkDelay,
    backgroundImage,
    setBackgroundImage,
    backgroundSize,
    setBackgroundSize,
    smokeEffectEnabled,
    setSmokeEffectEnabled,
    logo,
    setLogo,
    favicon,
    setFavicon,
    logoBackgroundColor,
    setLogoBackgroundColor,
    logoBackgroundTransparent,
    setLogoBackgroundTransparent,
    siteTitleColor,
    setSiteTitleColor,
    googleMapsApiKey,
    setGoogleMapsApiKey,
    splashTheme,
    setSplashTheme,
    googleClientId,
    setGoogleClientId,
    googleClientSecret,
    setGoogleClientSecret,
    splashAdEnabled,
    setSplashAdEnabled,
    splashAdUrl,
    setSplashAdUrl,
    categories,
    addCategory,
    games,
    addGame,
    updateGame,
    deleteGame,
    toggleFavorite,
    rateGame,
    addComment,
    loadApprovedComments,
    slider,
    setSlider,
    giveaway,
    recordDownload,
    recentDownloads,
    refresh,
    isLoaded,
    isLoading,
    // Pagination
    pagination,
    gamesLoading,
    loadGamesPage,
    // Programs
    programs,
    loadProgramsByCategory,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = () => {
  const ctx = React.useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within <StoreProvider />");
  return ctx;
};
